import './css/fonts.css'
import './css/global.css'
import './css/buttons.css'
import './css/accordions.css'
import './css/navigation.css'
import './css/lang-toggler.css'
import './css/project-visualization.css'

/* ---------- LINE ANIMATION ---------- */

import { animate, svg, onScroll } from 'animejs';

const wrapperEl = document.getElementById('wrapper')!;
const wrapperSentinel = document.getElementById('wrapper-sentinel')!;
const svgEl     = document.getElementById('svg-overlay')!;
const lineEl    = document.getElementById('line')!;
const circleEl  = document.getElementById('circle')!;

const topBullet    = document.getElementById('top-bullet')!;
const bottomBullet = document.getElementById('bottom-bullet')!;
let initialPointArr: [number, number][] = [];

let minRadius = 0;
let maxRadius = 0;
let totalZigs = 0;
let arrY = [];
let arrX = [];
let points: [number, number][] = [];

function drawViewbox() {
    const W = wrapperEl.clientWidth;
    const H = wrapperEl.clientHeight;

    svgEl.setAttribute('width', String(W));
    svgEl.setAttribute('height', String(H));
    svgEl.setAttribute('viewBox', `0 0 ${W} ${H}`);
    svgEl.style.height = H + 'px';

    wrapperSentinel.style.height = H + 'px';
}

function drawSvg() {
    let pathString = '';

    for (let i = 0; i < points.length; i++) {
        pathString += points[i][0] + ',' + points[i][1] + ' ';
    }

    lineEl.setAttribute('points', pathString.trim());
}

function reduceSVG() {
    points = initialPointArr.map(p => [...p]);
}

function expandSVG() {
    points = initialPointArr.map(p => [...p]);

    const newPoint:[number,number] = [points[points.length - 3][0], points[points.length - 1][1]];

    points.length -= 1;
    points.push(newPoint);

    const bottomRect = bottomBullet.getBoundingClientRect();
    const svgRect = svgEl.getBoundingClientRect();

    let endX = bottomRect.left - svgRect.left + bottomRect.width / 2;
    let endY = bottomRect.top - svgRect.top + bottomRect.height / 2;

    points.push([endX, endY]);
}

function updateSVG() {
    points.length = 0

    drawViewbox();

    const W = wrapperEl.clientWidth;
    const H = wrapperEl.clientHeight;
    const topRect = topBullet.getBoundingClientRect();
    const bottomRect = bottomBullet.getBoundingClientRect();
    const svgRect = svgEl.getBoundingClientRect();

    minRadius = topRect.width;
    maxRadius = bottomRect.width;

    const lambda = topRect.height / 2;

    // Convert to SVG local coords
    let startX = topRect.left - svgRect.left + topRect.width / 2;
    let endX = bottomRect.left - svgRect.left + bottomRect.width / 2;

    let startY = topRect.top - svgRect.top + topRect.height / 2;
    let endY = bottomRect.top - svgRect.top + bottomRect.height / 2;

    // Clamp X
    startX = Math.max(lambda, Math.min(W - lambda, startX));
    endX = Math.max(lambda, Math.min(W - lambda, endX));

    const minY = startY;
    const maxY = endY;

    // Circle
    circleEl.setAttribute('cx', String(startX));
    circleEl.setAttribute('cy', String(minY));
    circleEl.setAttribute('r', String(lambda));

    // Zig count
    const aspectRatio = W / H;
    let zigCount = 3;
    const desktop = window.matchMedia('(min-width: 768px)').matches;

    zigCount = desktop ? 2 : 2;
    if (aspectRatio <= 1) zigCount = desktop ? 4 : 2;
    if (aspectRatio <= 1 / 2) zigCount = desktop ? 6 : 4;
    if (aspectRatio <= 1 / 4) zigCount = desktop ? 8 : 6;
    if (aspectRatio <= 1 / 8) zigCount = desktop ? 10 : 8;

    const startRight = startX <= W / 2;

    totalZigs = zigCount;
    arrX.length = totalZigs;
    arrY.length = totalZigs;

    const totalHeight = maxY - minY;

    // 🔥 STEP 1: build decreasing weights
    const weights: number[] = [];
    for (let i = 0; i < totalZigs; i++) {
        weights.push(totalZigs - i + 1);
    }

    // 🔥 STEP 2: normalize → factors (sum = 1)
    const weightSum = weights.reduce((a, b) => a + b, 0);
    const factors = weights.map(w => w / weightSum);

    // 🔥 STEP 3: convert to positions
    let lastY = minY;

    for (let i = 0; i < totalZigs; i++) {
        const factor = factors[i];

        const shift = totalHeight * factor;
        const y = lastY + shift;

        // normalized position (for radius interpolation)
        const t = (y - minY) / totalHeight;

        const lambda = (minRadius + (maxRadius - minRadius) * t) / 2;

        arrY[i] = y;
        arrX[i] = lambda;

        lastY = y;
    }

    points.push([startX, minY]);

    for (let i = 0; i < totalZigs - 1; i++) {

        const minX = arrX[i];
        const maxX = W - arrX[i];

        const x = (i % 2 === 0)
            ? (startRight ? maxX : minX)
            : (startRight ? minX : maxX);

        points.push([x, arrY[i]]);
    }

    points.push([endX, maxY]);
    initialPointArr = points;

    drawSvg();
}

function init() {
    updateSVG();
    updateStrokeWidth();
}

init();

let lastWidth = window.innerWidth;
let resizeTimer;

window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => {
        const currentWidth = window.innerWidth;

        if (currentWidth !== lastWidth) {
            lastWidth = currentWidth;
            updateSVG();
            updateStrokeWidth();
        }
    }, 0);
});

window.addEventListener('load', init);

function updateStrokeWidth() {
    const rect = wrapperSentinel.getBoundingClientRect();

    const denominator = (rect.height - window.innerHeight) || 1;

    const progress = Math.min(
        Math.max(-rect.top / denominator, 0),
        1
    );

    const width = minRadius + (maxRadius - minRadius) * progress;

    lineEl.setAttribute('stroke-width', String(width));
    circleEl.setAttribute('r', String(width / 2));
}

/* ---------- ANIM JS ---------- */

const anim = animate(svg.createDrawable(lineEl), {
    draw: ['0 1'],
    ease: 'ease-in',
    autoplay: false,
});

onScroll({
    target: wrapperSentinel,
    enter: 'top top',
    leave: 'bottom bottom',
    sync: 0.5,
}).link(anim);

/* ---------- TOGGLE MENU ---------- */

const toggleMenu = document.querySelector('#toggle-menu');
const mainNav = document.querySelector('#main-navigation');
let scrollPosition = 0;
let isOpen = false;

if (toggleMenu) {

    mainNav.setAttribute('aria-hidden', 'false');
    mainNav.setAttribute('tabindex', '-1');
    mainNav.setAttribute('inert','');

    toggleMenu.addEventListener('click', () => {
        isOpen = toggleMenu.classList.toggle('is-open')
        mainNav.classList.toggle('is-open')

        if (mainNav.classList.contains('is-open')) {
            mainNav.setAttribute('aria-hidden', 'false');
            mainNav.removeAttribute('inert');
            mainNav.removeAttribute('tabindex');
        } else {
            mainNav.setAttribute('aria-hidden', 'true');
            mainNav.setAttribute('inert', '');
            mainNav.setAttribute('tabindex', '-1');
        }

        const delay = isOpen ? 200 : 0;

        setTimeout(() => {
            if (isOpen) {

                // Save scroll position
                scrollPosition = window.scrollY;

                // Lock scroll
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollPosition}px`;
                document.body.style.left = '0';
                document.body.style.right = '0';
            } else {
                // Unlock scroll
                document.body.style.position = '';
                document.body.style.top = '';
                document.body.style.left = '';
                document.body.style.right = '';

                // Restore scroll position
                window.scrollTo(0, scrollPosition);
            }
        }, delay)
    })

    let lastScrollPos = window.scrollY;
    let ticking = false;

    window.addEventListener('scroll', () => {
        if (!ticking && !isOpen) {
            window.requestAnimationFrame(() => {
                const currentScroll = window.scrollY;
                const scrollingDown = currentScroll > lastScrollPos;

                const footer = document.querySelector('footer')
                const footerPos = footer.getBoundingClientRect().top;
                const wrapperSentinelPos = Math.round(wrapperSentinel.getBoundingClientRect().bottom);

                if(footerPos > wrapperSentinelPos + 1) {
                    expandSVG();
                    drawViewbox();
                    drawSvg();
                } else if (footerPos < wrapperSentinelPos - 1) {
                    reduceSVG();
                    drawViewbox();
                    drawSvg();
                }

                if(currentScroll != lastScrollPos) {
                    updateStrokeWidth();
                }

                // Toggle visibility
                toggleMenu?.classList.toggle('scroll-down', scrollingDown);
                toggleMenu?.classList.toggle('fixed!', (!scrollingDown && currentScroll > 0) || (scrollingDown && currentScroll > 120));
                toggleMenu?.classList.toggle('is-hidden', scrollingDown && currentScroll > 120);
                toggleMenu?.classList.toggle('transition-none', (!scrollingDown && currentScroll < 120) || (scrollingDown && currentScroll <= 200));

                lastScrollPos = currentScroll;
                ticking = false;
            });

            ticking = true;


        }
    });
}

/* ---------- NAV-TOGGLER ---------- */

document.querySelectorAll('button.nav-link[aria-controls]').forEach(button => {
    button.addEventListener('click', () => {
        const isOpen = button.getAttribute('aria-expanded') === 'true';

        const nav = button.closest('nav#navigation');
        nav?.querySelectorAll('button.nav-link[aria-controls]').forEach(other => {
            if (other !== button) {
                other.setAttribute('aria-expanded', 'false');
            }
        });

        button.setAttribute('aria-expanded', isOpen?'false':'true');
    });
});

/* ---------- LANG-TOGGLER ---------- */

const langNav = document.querySelector('#lang-nav');
if (langNav) {
    langNav.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        langNav.classList.toggle('is-active', !langNav.classList.contains('is-active'));
        setTimeout(() => {
            window.location.href = langNav.getAttribute('href');
        }, 100);
    });
}

/* ---------- ACCORDION ---------- */

const allAccordions = document.querySelectorAll('.js-accordion');

allAccordions.forEach(accordion => {

    const buttons = accordion.querySelectorAll('button[aria-controls]');

    buttons.forEach(button => {
        button.addEventListener('click', () => {
            const isOpen = button.getAttribute('aria-expanded') === 'true';
            button.setAttribute('aria-expanded', !isOpen?'true':'false');
        });
    });
});

document.addEventListener('click', (e) => {
    const target = e.target;

    if (!(target instanceof Node)) return;

    allAccordions.forEach(accordion => {
        accordion.querySelectorAll('button[aria-controls][data-reactive]').forEach(button => {
            if (!button.contains(target)) {
                button.setAttribute('aria-expanded', 'false');
            }
        });
    });
});

/* ---------- PROJECT VISUALISATION ---------- */

document.querySelectorAll('.js-projectVisualization').forEach(projectVisualization => {
    const projectButtons = projectVisualization.querySelectorAll('.legend-button[aria-controls]');

    projectButtons.forEach(button => {
        button.addEventListener('click', () => {

            let relatedToggler = null;

            const isVisible = button.getAttribute('aria-expanded') === 'false';

            button.setAttribute('aria-expanded', isVisible? 'true':'false');

            const targetId = button.getAttribute('aria-controls');
            const targetImage = projectVisualization.querySelector(`#${targetId}`);
            targetImage.classList.toggle('is-hidden', !isVisible);

            if (button.getAttribute('data-sync')) {
                const relatedId = button.getAttribute('data-sync')
                const syncImage = projectVisualization.querySelector(`#${relatedId}`);
                const syncToggler = projectVisualization.querySelector(`.legend-button[aria-controls="${relatedId}"]`);
                syncImage.classList.toggle('is-hidden', !isVisible);
                syncToggler.setAttribute('aria-expanded', isVisible? 'true':'false');
            }

            if (isVisible) {
                if (button.getAttribute('data-controls') && button.getAttribute('aria-expanded') === 'true') {
                    const relatedId = button.getAttribute('data-controls')
                    const relatedImage = projectVisualization.querySelector(`#${relatedId}`);
                    relatedToggler = projectVisualization.querySelector(`.legend-button[aria-controls="${relatedId}"]`);
                    relatedImage.classList.remove('is-hidden');
                    relatedToggler.setAttribute('aria-expanded','true');
                }
            }

            if (button.getAttribute('data-hide') && button.getAttribute('data-hide') === 'others') {

                projectButtons.forEach(otherButton => {
                    if (otherButton !== button && otherButton !== relatedToggler) {
                        const relatedImageId = otherButton.getAttribute('aria-controls');
                        const relatedImage = projectVisualization.querySelector(`#${relatedImageId}`);
                        relatedImage.classList.add('is-hidden');
                        otherButton.setAttribute('aria-expanded','false');
                    }
                });
            }

            if (isVisible) {
                if (button.getAttribute('data-show') && button.getAttribute('data-show') === 'others') {

                    projectButtons.forEach(otherButton => {
                        if (otherButton !== button && otherButton !== relatedToggler) {
                            const relatedImageId = otherButton.getAttribute('aria-controls');
                            const relatedImage = projectVisualization.querySelector(`#${relatedImageId}`);
                            relatedImage.classList.remove('is-hidden');
                            otherButton.setAttribute('aria-expanded','true');
                        }
                    });
                }
            }
        });
    });
});

/* ---------- OVERVIEW BACK ---------- */

const overviewBack = document.getElementById('Overview-back');

if (overviewBack) {
    overviewBack.addEventListener('click', function (e) {
        e.preventDefault();

        const targetUrl = overviewBack.dataset.href;
        const referrer = document.referrer;

        if (referrer && referrer.startsWith(targetUrl)) {
            history.back();
        } else {
            window.location.href = targetUrl;
        }
    });
}